import { formatMoney } from '@belong/common';
import { Divider, Text } from '@belong/ui';
import Collapsible from 'components/Collapsible/Collapsible';
import { MaintenanceInvoiceConsumerModel } from 'maintenance/types/invoice';
import { CostItemsGroup } from './CostItemsGroup';

type QuoteCentricReceiptSectionProps = {
  invoices: MaintenanceInvoiceConsumerModel[];
};

const InvoiceTitleComponent = ({
  title,
  totalAmount,
  responsibilityPercentage,
}: {
  title: string;
  totalAmount: number;
  responsibilityPercentage: number;
}) => (
  <div className="flex flex-1 items-center justify-between pr-sm">
    <Text fontWeight="semibold">{title}</Text>
    <Text fontWeight="semibold" className="whitespace-nowrap text-navy">
      {formatMoney(
        responsibilityPercentage < 100
          ? totalAmount * (responsibilityPercentage / 100)
          : totalAmount,
      )}
    </Text>
  </div>
);

const InvoiceCardInner = ({
  invoice,
}: {
  invoice: MaintenanceInvoiceConsumerModel;
}) => (
  <div className="px-sm pb-sm">
    {invoice.assignedProName && (
      <Text variant="p1" className="text-dark-gray">
        {invoice.assignedProName}
      </Text>
    )}

    {invoice.scopeOfWork && (
      <Text className="mt-sm">{invoice.scopeOfWork}</Text>
    )}

    {invoice.costItems.length > 0 && (
      <div className="mt-sm">
        <CostItemsGroup costItems={invoice.costItems} />
      </div>
    )}

    <Divider borderStyle="dashed" className="my-sm" />

    <div className="flex justify-between">
      <Text fontWeight="semibold">Total</Text>
      <Text fontWeight="semibold" className="text-navy">
        {formatMoney(invoice.totalAmount)}
      </Text>
    </div>

    {invoice.responsibilityPercentage < 100 && (
      <div className="mt-xs flex justify-between">
        <Text fontWeight="semibold">
          Your Portion ({invoice.responsibilityPercentage}%)
        </Text>
        <Text fontWeight="semibold" className="text-navy">
          {invoice.responsibilityPercentage > 0
            ? formatMoney(
                invoice.totalAmount * (invoice.responsibilityPercentage / 100),
              )
            : 'Free'}
        </Text>
      </div>
    )}
  </div>
);

export const QuoteCentricReceiptSection = ({
  invoices,
}: QuoteCentricReceiptSectionProps) => {
  const published = invoices.filter((inv) => inv.status === 'Published');
  if (published.length === 0) return null;

  return (
    <div className="py-sm">
      <Collapsible
        title={published.length > 1 ? 'Receipts' : 'Receipt'}
        collapsed
        inner={
          <div className="pt-sm">
            {published.map((invoice) => (
              <div
                key={invoice.uniqueId}
                className="mb-sm rounded border border-solid border-gray bg-light-gray"
              >
                <Collapsible
                  titleCustomClassName="items-center p-sm"
                  titleComponent={
                    <InvoiceTitleComponent
                      title={invoice.title}
                      totalAmount={invoice.totalAmount}
                      responsibilityPercentage={
                        invoice.responsibilityPercentage
                      }
                    />
                  }
                  title={invoice.title}
                  collapsed
                  inner={<InvoiceCardInner invoice={invoice} />}
                />
              </div>
            ))}
          </div>
        }
      />
    </div>
  );
};
